import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Mic, Video, Building, Award, Users, Plus, ExternalLink, ChevronRight, Star, MessageSquare, AlertTriangle, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_ICONS = { appearance: Mic, endorsement: Award, activation: Building };
const TYPE_COLORS = { appearance: 'text-blue-400 bg-blue-400/10', endorsement: 'text-yellow-400 bg-yellow-400/10', activation: 'text-[#10B981] bg-[#10B981]/10' };
const HEALTH_COLORS = { Strong: 'text-[#10B981]', Active: 'text-yellow-400', Cooling: 'text-orange-400', 'At Risk': 'text-[#FF4343]' };

function formatNum(n) { if (!n) return '0'; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toString(); }

// ==================== ADMIN ENGAGEMENTS ====================
export function AdminEngagementsTab() {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '', engagement_type: 'appearance', engagement_subtype: 'podcast',
    description: '', deliverables: [], event_date: '', event_time: '', event_location_type: 'virtual',
    event_location: '', event_name: '', compensation_type: 'paid', base_fee: '', spots_total: 1, brand_id: ''
  });
  const [brands, setBrands] = useState([]);
  const [delInput, setDelInput] = useState('');

  useEffect(() => {
    axios.get(`${API}/engagements`, { headers: getAuthHeaders() }).then(r => setEngagements(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/brands/v2`, { headers: getAuthHeaders() }).then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = { ...form, base_fee: form.base_fee ? parseFloat(form.base_fee) : null, spots_total: parseInt(form.spots_total) || 1 };
      await axios.post(`${API}/engagements`, payload, { headers: getAuthHeaders() });
      const { data } = await axios.get(`${API}/engagements`, { headers: getAuthHeaders() });
      setEngagements(data);
      setForm({ ...form, title: '', description: '', deliverables: [], event_date: '' });
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const SUBTYPES = {
    appearance: ['podcast', 'webinar', 'trade_show', 'keynote', 'panel', 'roundtable', 'awards', 'site_visit', 'demo_event', 'other'],
    endorsement: ['white_paper', 'advisory_board', 'product_page', 'press_release', 'award_judge', 'quote', 'other'],
    activation: ['hosted_event', 'brand_intro', 'facility_demo', 'other'],
  };

  return (
    <div data-testid="admin-engagements-tab">
      {/* Create Form */}
      <div className="bg-[#111827] border border-white/10 p-5 mb-6">
        <h3 className="text-sm font-semibold mb-4">Create Engagement</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          {['appearance', 'endorsement', 'activation'].map(t => {
            const Icon = TYPE_ICONS[t];
            return (
              <button key={t} onClick={() => setForm({...form, engagement_type: t, engagement_subtype: SUBTYPES[t][0]})}
                className={`p-3 border text-left ${form.engagement_type === t ? 'border-[#FF4343] bg-[#FF4343]/5' : 'border-white/10'}`}>
                <Icon className="w-5 h-5 text-[#FF4343] mb-1" />
                <span className="text-xs font-semibold text-white capitalize">{t}</span>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <select value={form.brand_id} onChange={e => setForm({...form, brand_id: e.target.value})}
            className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
            <option value="">Select Brand</option>
            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={form.engagement_subtype} onChange={e => setForm({...form, engagement_subtype: e.target.value})}
            className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm capitalize focus:outline-none focus:border-[#FF4343]">
            {(SUBTYPES[form.engagement_type] || []).map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input placeholder="Engagement Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
            className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
          <input placeholder="Event Name (e.g., The Auto Podcast)" value={form.event_name} onChange={e => setForm({...form, event_name: e.target.value})}
            className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})}
            className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
          <input placeholder="Base Fee ($)" type="number" value={form.base_fee} onChange={e => setForm({...form, base_fee: e.target.value})}
            className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
          <select value={form.event_location_type} onChange={e => setForm({...form, event_location_type: e.target.value})}
            className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
            <option value="virtual">Virtual</option><option value="in_person">In Person</option><option value="hybrid">Hybrid</option>
          </select>
        </div>
        {/* Deliverables */}
        <div className="mb-3">
          <div className="flex flex-wrap gap-1 mb-1">
            {form.deliverables.map((d, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/20">
                {d} <button onClick={() => setForm({...form, deliverables: form.deliverables.filter((_,j) => j !== i)})}>&times;</button>
              </span>
            ))}
          </div>
          <input value={delInput} onChange={e => setDelInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && delInput.trim()) { e.preventDefault(); setForm({...form, deliverables: [...form.deliverables, delInput.trim()]}); setDelInput(''); }}}
            placeholder="Add deliverable + Enter"
            className="w-full px-3 py-1.5 bg-[#090E33] border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
        </div>
        <button onClick={handleCreate} disabled={creating || !form.title || !form.brand_id}
          className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
          {creating ? 'Creating...' : 'Create Engagement'}
        </button>
      </div>

      {/* Engagements List */}
      {loading ? <p className="text-[#9CA3AF]">Loading...</p> : engagements.length === 0 ? (
        <p className="text-[#6B7280] text-sm">No engagements yet.</p>
      ) : (
        <div className="space-y-2">
          {engagements.map(e => {
            const Icon = TYPE_ICONS[e.engagement_type] || Mic;
            return (
              <div key={e.id} className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between" data-testid={`eng-${e.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center ${TYPE_COLORS[e.engagement_type] || 'bg-white/5'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-sm text-white font-medium">{e.title}</span>
                    <div className="flex gap-2 text-[10px] text-[#6B7280] mt-0.5">
                      <span>{e.brand_name}</span>
                      <span className="capitalize">{e.engagement_subtype?.replace(/_/g,' ')}</span>
                      {e.event_date && <span>{e.event_date}</span>}
                      {e.base_fee && <span className="font-mono text-[#FF4343]">${e.base_fee}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-mono text-[#6B7280]">{e.participant_count || 0}/{e.spots_total} spots</span>
                  <span className={`text-[10px] font-mono uppercase ${e.status === 'completed' ? 'text-[#10B981]' : e.status === 'draft' ? 'text-[#6B7280]' : 'text-[#FF4343]'}`}>{e.status}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== BRAND PORTAL ALLIES ====================
export function BrandAlliesSection({ getHeaders: gh }) {
  const [allies, setAllies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');

  const headers = gh ? gh() : {};

  useEffect(() => {
    axios.get(`${API}/portal/allies`, { headers }).then(r => setAllies(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const sendMessage = async () => {
    if (!selected || !message.trim()) return;
    try {
      await axios.post(`${API}/portal/allies/${selected.id}/message`, { message, sender_type: 'brand' }, { headers });
      setMessage('');
      const { data } = await axios.get(`${API}/portal/allies/${selected.id}`, { headers });
      setSelected(data);
    } catch {}
  };

  if (selected) {
    return (
      <div data-testid="ally-detail">
        <button onClick={() => setSelected(null)} className="text-xs text-[#9CA3AF] hover:text-white mb-4">&larr; Back to Roster</button>
        <div className="bg-[#111827] border border-white/10 p-5 mb-4 border-l-[3px] border-l-[#FF4343]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white">{selected.name}</h3>
              <p className="text-xs text-[#9CA3AF]">{selected.title} · {selected.company}</p>
            </div>
            <span className={`text-xs font-mono px-2 py-1 ${HEALTH_COLORS[selected.health_label] || 'text-[#6B7280]'} bg-white/5`}>{selected.health_label} ({selected.relationship_health_score})</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-[#111827] border border-white/10 p-3 text-center"><span className="font-mono text-xl text-white">{selected.total_engagements || 0}</span><p className="text-[8px] text-[#6B7280] font-mono uppercase">Engagements</p></div>
          <div className="bg-[#111827] border border-white/10 p-3 text-center"><span className="font-mono text-xl text-white">{selected.total_content_pieces || 0}</span><p className="text-[8px] text-[#6B7280] font-mono uppercase">Content</p></div>
          <div className="bg-[#111827] border border-white/10 p-3 text-center">
            <span className={`font-mono text-xl ${selected.exclusivity_active ? 'text-[#FF4343]' : 'text-[#6B7280]'}`}>{selected.exclusivity_active ? 'Active' : 'None'}</span>
            <p className="text-[8px] text-[#6B7280] font-mono uppercase">Exclusivity</p>
          </div>
        </div>
        {/* Messages */}
        <div className="bg-[#111827] border border-white/10 p-4 mb-4">
          <h4 className="text-xs font-semibold mb-3">Messages</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto mb-3">
            {(selected.messages || []).length === 0 ? <p className="text-[10px] text-[#6B7280]">No messages yet.</p> : (
              selected.messages.map(m => (
                <div key={m.id} className={`p-2 text-xs ${m.sender_type === 'brand' ? 'bg-[#FF4343]/5 border-l-2 border-[#FF4343] ml-8' : 'bg-white/5 mr-8'}`}>
                  <span className="text-[9px] text-[#6B7280]">{m.sender_type} · {new Date(m.created_at).toLocaleString()}</span>
                  <p className="text-[#9CA3AF] mt-0.5">{m.message}</p>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Type a message..."
              onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
              className="flex-1 px-3 py-2 bg-[#090E33] border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
            <button onClick={sendMessage} disabled={!message.trim()}
              className="border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] px-3 py-1 text-xs transition-colors disabled:opacity-50">
              <MessageSquare className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {/* Engagement History */}
        {selected.engagement_history?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-2">Engagement History</h4>
            <div className="space-y-1">
              {selected.engagement_history.map(e => (
                <div key={e.id} className="bg-[#111827] border border-white/10 p-3 flex items-center justify-between text-xs">
                  <div><span className="text-white">{e.title}</span><span className="text-[#6B7280] ml-2 capitalize">{e.engagement_type}</span></div>
                  <span className="text-[#6B7280]">{e.event_date}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) return <p className="text-[#9CA3AF]">Loading allies...</p>;

  return (
    <div data-testid="brand-allies">
      <h3 className="text-sm font-semibold mb-4">Industry Allies</h3>
      {allies.length === 0 ? (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <Users className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#6B7280] text-sm">No allies yet. Executives are added automatically after successful engagements with 4+ star ratings.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {allies.map(a => (
            <div key={a.id} className="bg-[#111827] border border-white/10 p-4 cursor-pointer hover:border-white/20 transition-colors"
              onClick={() => { axios.get(`${API}/portal/allies/${a.id}`, { headers }).then(r => setSelected(r.data)).catch(() => {}); }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/5 flex items-center justify-center text-[#FF4343] font-bold text-sm">
                    {a.name ? a.name[0] : '?'}
                  </div>
                  <div>
                    <span className="text-sm text-white font-medium">{a.name}</span>
                    <p className="text-[10px] text-[#6B7280]">{a.title} · {a.company}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-mono ${HEALTH_COLORS[a.health_label] || 'text-[#6B7280]'}`}>{a.health_label}</span>
              </div>
              <div className="flex gap-3 text-[10px] text-[#6B7280]">
                <span>{a.total_engagements || 0} engagements</span>
                {a.last_engagement_at && <span>Last: {new Date(a.last_engagement_at).toLocaleDateString()}</span>}
                {a.unread_messages > 0 && <span className="text-[#FF4343] font-mono">{a.unread_messages} unread</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
